import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Sparkles, Inbox, LayoutGrid, Zap, Users, ScrollText, ShieldAlert, BarChart3, MessageCircle, Bell, Mail } from "lucide-react";
import api from "@/lib/api";
import { Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";

const EDITORIAL_STATES = [
  "borrador", "en_revision", "verificacion", "revision_legal", "solicitud_respuesta",
  "aprobado", "programado", "publicado", "actualizado", "corregido", "archivado",
];
const PROMISE_STATES = ["cumplida", "parcialmente_cumplida", "en_progreso", "estancada", "incumplida", "abandonada", "no_verificable"];
const PETITION_STATES = ["borrador", "en_revision", "requiere_evidencia", "aprobada", "publicada", "meta_alcanzada", "entregada", "recibida", "esperando_respuesta", "contestada", "accion_tomada", "cerrada", "archivada"];

// field: [key, label, type]  type: text|textarea|number|status|promise_status|entity_type|content_type|bool|json
const SCHEMAS = {
  consultations: {
    label: "Consultas",
    fields: [
      ["title", "Título", "text"], ["category", "Categoría", "text"],
      ["context", "Contexto neutral", "textarea"],
      ["options", "Opciones [{id,label}]", "json"],
      ["arguments_for", "Argumentos a favor [{text,source}]", "json"],
      ["arguments_against", "Argumentos en contra [{text,source}]", "json"],
      ["sources", "Fuentes [url]", "json"],
      ["methodology", "Metodología", "textarea"],
      ["hide_results_until_vote", "Ocultar resultados hasta votar", "bool"],
      ["featured", "Destacada", "bool"],
      ["status", "Estado editorial", "status"],
    ],
    defaults: { options: [{ id: "a", label: "Sí" }, { id: "b", label: "No" }], arguments_for: [], arguments_against: [], sources: [], hide_results_until_vote: true, featured: false, status: "borrador", methodology: "Participación entre usuarios verificados de 2Voto. No representa una muestra científica de toda la población." },
  },
  investigations: {
    label: "Fiscalización",
    fields: [
      ["title", "Título", "text"], ["summary", "Resumen ejecutivo", "textarea"],
      ["body", "Cuerpo", "textarea"], ["content_type", "Tipo de contenido", "content_type"],
      ["entities_involved", "Entidades [nombre]", "json"],
      ["timeline", "Cronología [{date,event}]", "json"],
      ["sources", "Fuentes [url]", "json"], ["amount", "Monto ($)", "number"],
      ["open_questions", "Preguntas pendientes [texto]", "json"],
      ["official_response", "Respuesta oficial", "textarea"],
      ["investigation_status", "Estado de investigación", "text"],
      ["featured", "Destacada", "bool"], ["status", "Estado editorial", "status"],
    ],
    defaults: { entities_involved: [], timeline: [], sources: [], open_questions: [], content_type: "investigacion", investigation_status: "bajo_investigacion", featured: false, status: "borrador" },
  },
  promises: {
    label: "Promesómetro",
    fields: [
      ["title", "Título", "text"], ["description", "Descripción", "textarea"],
      ["responsible", "Responsable", "text"], ["promised_date", "Fecha prometida", "text"],
      ["source", "Fuente", "text"], ["budget", "Presupuesto ($)", "number"],
      ["progress", "Avance (0-100)", "number"], ["promise_status", "Clasificación", "promise_status"],
      ["evidence", "Evidencia [url]", "json"], ["status", "Estado editorial", "status"],
    ],
    defaults: { evidence: [], progress: 0, promise_status: "en_progreso", status: "borrador" },
  },
  proposals: {
    label: "Propuestas",
    fields: [
      ["title", "Título", "text"], ["theme", "Tema", "text"], ["problem", "Problema", "textarea"],
      ["evidence", "Evidencia", "textarea"], ["objective", "Objetivo", "textarea"],
      ["solution", "Solución", "textarea"], ["estimated_cost", "Costo estimado", "text"],
      ["funding_source", "Financiamiento", "text"], ["timeline_text", "Calendario", "text"],
      ["responsible", "Responsables", "text"], ["success_metrics", "Métricas", "textarea"],
      ["risks", "Riesgos", "textarea"], ["international_comparison", "Comparación intl.", "textarea"],
      ["expert_opinion", "Opinión de expertos", "textarea"], ["status", "Estado editorial", "status"],
    ],
    defaults: { status: "borrador" },
  },
  entities: {
    label: "Perfiles",
    fields: [
      ["name", "Nombre", "text"], ["entity_type", "Tipo", "entity_type"],
      ["role_title", "Cargo/rol", "text"], ["description", "Descripción", "textarea"],
      ["status", "Estado", "status"],
    ],
    defaults: { entity_type: "agencia", status: "publicado" },
  },
  documents: {
    label: "Biblioteca",
    fields: [
      ["title", "Título", "text"], ["category", "Categoría", "text"],
      ["description", "Descripción", "textarea"], ["source_url", "URL de la fuente", "text"],
      ["status", "Estado", "status"],
    ],
    defaults: { status: "publicado" },
  },
  flash_polls: {
    label: "Relámpago",
    fields: [
      ["title", "Título interno", "text"], ["poll_type", "Tipo", "poll_type"],
      ["question", "Pregunta principal", "textarea"],
      ["options", "Opciones [{id,label}]", "json"],
      ["subquestion", "Subpregunta (opcional)", "textarea"],
      ["sub_options", "Sub-opciones [{id,label}]", "json"],
      ["context", "Contexto breve", "textarea"], ["source", "Fuente", "text"],
      ["opens_at", "Apertura (ISO)", "text"], ["closes_at", "Cierre (ISO)", "text"],
      ["results_mode", "Modo de resultados", "results_mode"],
      ["status", "Estado editorial", "status"],
    ],
    defaults: {
      poll_type: "actualidad",
      options: [{ id: "a", label: "Apruebo" }, { id: "b", label: "Desapruebo" }, { id: "c", label: "No tengo suficiente información" }],
      sub_options: [], results_mode: "auto", status: "borrador",
    },
  },
  petitions: {
    label: "Peticiones",
    fields: [
      ["title", "Título", "text"], ["addressed_to", "Dirigida a", "text"],
      ["action_requested", "Acción solicitada", "text"], ["problem", "Problema", "textarea"],
      ["justification", "Texto / justificación", "textarea"],
      ["sources", "Fuentes [url]", "json"], ["territory", "Territorio", "text"],
      ["goal", "Meta de firmas", "number"], ["deadline", "Fecha límite (ISO)", "text"],
      ["presumption_notice", "Aviso de presunción de inocencia", "bool"],
      ["creator_public", "Creador público", "bool"],
      ["official_response", "Respuesta oficial", "textarea"],
      ["timeline", "Cronología [{date,event}]", "json"],
      ["status", "Estado", "petition_status"],
    ],
    defaults: { sources: [], timeline: [], goal: 10000, presumption_notice: false, creator_public: false, status: "borrador" },
  },
};

const TABS = Object.keys(SCHEMAS);

const VALID_ROLES = ["citizen", "colaborador", "moderator", "verificador", "revisor_legal",
  "investigador", "editor", "director_editorial", "admin_pais", "admin", "superadmin"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("consultations");
  const [view, setView] = useState("content"); // content | reports
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState(null);
  const [reports, setReports] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [waStatus, setWaStatus] = useState(null);
  const [waLogs, setWaLogs] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const isSuper = user && ["admin", "superadmin"].includes(user.role);

  const loadOverview = useCallback(() => api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {}), []);
  const loadItems = useCallback(() => { setItems(null); api.get(`/admin/${tab}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [tab]);
  const loadReports = useCallback(() => { setReports(null); api.get("/admin/reports/inbox").then((r) => setReports(r.data)).catch(() => setReports([])); }, []);
  const loadProposals = useCallback(() => { setProposals(null); api.get("/admin/flash/proposals").then((r) => setProposals(r.data)).catch(() => setProposals([])); }, []);
  const loadUsers = useCallback(() => { setUsers(null); api.get("/admin/users", { params: userQuery ? { q: userQuery } : {} }).then((r) => setUsers(r.data)).catch(() => setUsers([])); }, [userQuery]);
  const loadAudit = useCallback(() => { setAudit(null); api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => setAudit([])); }, []);
  const loadWaLogs = useCallback(() => { setWaLogs(null); api.get("/admin/whatsapp/logs").then((r) => setWaLogs(r.data)).catch(() => setWaLogs([])); }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (view === "content") loadItems(); }, [view, loadItems]);
  useEffect(() => { if (view === "reports") loadReports(); }, [view, loadReports]);
  useEffect(() => { if (view === "proposals") loadProposals(); }, [view, loadProposals]);
  useEffect(() => { if (view === "users") loadUsers(); }, [view, loadUsers]);
  useEffect(() => { if (view === "audit") loadAudit(); }, [view, loadAudit]);
  useEffect(() => { if (view === "whatsapp") loadWaLogs(); }, [view, loadWaLogs]);
  useEffect(() => {
    if (view === "content" && tab === "flash_polls") {
      api.get("/admin/whatsapp/status").then((r) => setWaStatus(r.data)).catch(() => setWaStatus(null));
      api.get("/admin/push/status").then((r) => setPushStatus(r.data)).catch(() => setPushStatus(null));
    }
    if (view === "content" && (tab === "flash_polls" || tab === "petitions")) {
      api.get("/admin/email/status").then((r) => setEmailStatus(r.data)).catch(() => setEmailStatus(null));
    }
  }, [view, tab]);

  const changeRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, { role }); toast.success("Rol actualizado."); loadUsers(); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar."); }
  };
  const deleteUser = async (uid, email) => {
    if (!window.confirm(`¿Eliminar al usuario ${email}? Se borrarán sus votos y firmas. Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("Usuario eliminado."); loadUsers(); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar."); }
  };

  const sendWhatsApp = async (id, title) => {
    if (!window.confirm(`¿Enviar "${title}" por WhatsApp a todos los usuarios que aceptaron recibir avisos?`)) return;
    const tid = toast.loading("Enviando por WhatsApp…");
    try {
      const r = await api.post(`/admin/flash/${id}/whatsapp`, { base_url: window.location.origin });
      toast.success(`Enviados: ${r.data.sent} · Fallidos: ${r.data.failed} de ${r.data.recipients} destinatarios.`, { id: tid });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar.", { id: tid }); }
  };

  const sendPush = async (id, title) => {
    if (!window.confirm(`¿Enviar notificación push de "${title}" a todos los suscriptores?`)) return;
    const tid = toast.loading("Enviando notificación push…");
    try {
      const r = await api.post(`/admin/flash/${id}/push`, { base_url: window.location.origin });
      toast.success(`Push enviadas: ${r.data.sent} de ${r.data.subscribers} suscriptores.`, { id: tid });
      api.get("/admin/push/status").then((res) => setPushStatus(res.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar la notificación.", { id: tid }); }
  };

  const sendEmail = async (id, title, kind) => {
    const label = kind === "petition" ? "petición" : "encuesta";
    if (!window.confirm(`¿Enviar un correo de "${title}" a todos los usuarios registrados (${emailStatus?.recipients ?? 0} destinatarios)?`)) return;
    const tid = toast.loading("Enviando correos…");
    const endpoint = kind === "petition" ? `/admin/petitions/${id}/email` : `/admin/flash/${id}/email`;
    try {
      const r = await api.post(endpoint, { base_url: window.location.origin });
      toast.success(`Correos enviados: ${r.data.sent} · Fallidos: ${r.data.failed} de ${r.data.recipients} de la ${label}.`, { id: tid });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar el correo.", { id: tid }); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/admin/${tab}/${id}`);
    toast.success("Eliminado.");
    loadItems(); loadOverview();
  };

  const updateReportStatus = async (id, status) => {
    await api.put(`/admin/reports/${id}`, { internal_status: status });
    toast.success("Estado actualizado.");
    loadReports();
  };

  const updateProposalStatus = async (id, status) => {
    await api.put(`/admin/flash/proposals/${id}`, { review_status: status });
    toast.success("Estado actualizado.");
    loadProposals();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-heading font-black text-3xl text-slate-900">Panel CMS</h1>
        <span className="text-sm text-slate-500">{user?.email} · <span className="capitalize">{user?.role}</span></span>
      </div>
      <p className="text-slate-500 mb-8">Flujo editorial: borrador → revisión → verificación → legal → aprobado → publicado. Ningún reporte se publica automáticamente.</p>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
          {Object.entries(overview).map(([k, v]) => (
            <div key={k} className="bg-white rounded-lg border border-slate-200 p-3 text-center">
              <p className="font-heading font-black text-2xl text-slate-900">{v}</p>
              <p className="text-[11px] text-slate-500 capitalize">{k}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-6">
        <button onClick={() => setView("content")} data-testid="admin-view-content"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${view === "content" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          <LayoutGrid className="w-4 h-4" /> Contenido
        </button>
        <button onClick={() => setView("analytics")} data-testid="admin-view-analytics"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${view === "analytics" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          <BarChart3 className="w-4 h-4" /> Analítica
        </button>
        <button onClick={() => setView("reports")} data-testid="admin-view-reports"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${view === "reports" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          <Inbox className="w-4 h-4" /> Bandeja de reportes
        </button>
        <button onClick={() => setView("proposals")} data-testid="admin-view-proposals"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${view === "proposals" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          <Zap className="w-4 h-4" /> Preguntas propuestas
        </button>
        {isSuper && (
          <button onClick={() => setView("users")} data-testid="admin-view-users"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${view === "users" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            <Users className="w-4 h-4" /> Usuarios
          </button>
        )}
        <button onClick={() => setView("audit")} data-testid="admin-view-audit"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${view === "audit" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          <ScrollText className="w-4 h-4" /> Auditoría
        </button>
        <button onClick={() => setView("whatsapp")} data-testid="admin-view-whatsapp"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${view === "whatsapp" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          <MessageCircle className="w-4 h-4" /> Envíos WhatsApp
        </button>
      </div>

      {view === "content" && (
        <>
          <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-4">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === t ? "bg-cyan-50 text-cyan-700" : "text-slate-600 hover:bg-slate-100"}`}>
                {SCHEMAS[t].label}
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center mb-4">
            <h2 className="font-heading font-bold text-xl text-slate-900">{SCHEMAS[tab].label}</h2>
            <button onClick={() => setEditing({ ...SCHEMAS[tab].defaults })} data-testid="admin-create-button"
              className="inline-flex items-center gap-2 bg-rose-500 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-rose-600 transition-colors">
              <Plus className="w-4 h-4" /> Crear
            </button>
          </div>

          {tab === "flash_polls" && pushStatus && (
            <div data-testid="push-banner" className="mb-4 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-800 p-4 text-sm flex items-start gap-3">
              <Bell className="w-5 h-5 shrink-0 mt-0.5" />
              <p>Notificaciones push activas. <strong>{pushStatus.subscribers}</strong> dispositivo(s) suscrito(s). Usa el ícono de campana en cada encuesta para enviar una notificación instantánea.</p>
            </div>
          )}

          {tab === "flash_polls" && waStatus && (
            <div data-testid="whatsapp-banner" className={`mb-4 rounded-lg border p-4 text-sm flex items-start gap-3 ${waStatus.configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              <MessageCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                {waStatus.configured ? (
                  <p>WhatsApp activo{waStatus.has_template ? " (con plantilla aprobada)" : " (modo texto/sandbox)"}. <strong>{waStatus.opted_in_users}</strong> usuario(s) con opt-in. Usa el ícono de WhatsApp en cada encuesta para enviarla.</p>
                ) : (
                  <p>WhatsApp aún no está configurado. Agrega las credenciales de Twilio (<code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_WHATSAPP_FROM</code>) en el backend para habilitar el envío. Actualmente <strong>{waStatus.opted_in_users}</strong> usuario(s) con opt-in.</p>
                )}
              </div>
            </div>
          )}

          {(tab === "flash_polls" || tab === "petitions") && emailStatus && (
            <div data-testid="email-banner" className={`mb-4 rounded-lg border p-4 text-sm flex items-start gap-3 ${emailStatus.configured ? "bg-indigo-50 border-indigo-200 text-indigo-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              <Mail className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                {emailStatus.configured ? (
                  <p>Correo activo (envío desde <strong>{emailStatus.sender}</strong>). <strong>{emailStatus.recipients}</strong> destinatario(s). Usa el ícono de sobre en cada fila para enviar un correo elegante con el enlace para participar.</p>
                ) : (
                  <p>El correo aún no está configurado. Agrega <code>RESEND_API_KEY</code> en el backend para habilitar el envío por correo.</p>
                )}
              </div>
            </div>
          )}

          {items === null ? <Loading /> : (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              {items.length === 0 ? <p className="p-8 text-center text-slate-400">Sin elementos. Crea el primero.</p> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr><th className="p-3">Título</th><th className="p-3">Estado</th><th className="p-3 text-right">Acciones</th></tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-t border-slate-100" data-testid={`admin-row-${it.id}`}>
                        <td className="p-3 font-medium text-slate-800">{it.title || it.name}</td>
                        <td className="p-3"><span className="text-xs bg-slate-100 px-2 py-1 rounded-full">{it.status}</span></td>
                        <td className="p-3 text-right">
                          <button onClick={() => setEditing(it)} data-testid={`admin-edit-${it.id}`} className="p-2 text-slate-500 hover:text-cyan-600"><Pencil className="w-4 h-4" /></button>
                          {tab === "flash_polls" && (
                            <button onClick={() => sendWhatsApp(it.id, it.title)} data-testid={`admin-whatsapp-${it.id}`} title="Enviar por WhatsApp a usuarios con opt-in" className="p-2 text-slate-500 hover:text-emerald-600"><MessageCircle className="w-4 h-4" /></button>
                          )}
                          {tab === "flash_polls" && (
                            <button onClick={() => sendPush(it.id, it.title)} data-testid={`admin-push-${it.id}`} title="Enviar notificación push a suscriptores" className="p-2 text-slate-500 hover:text-cyan-600"><Bell className="w-4 h-4" /></button>
                          )}
                          {(tab === "flash_polls" || tab === "petitions") && emailStatus?.configured && (
                            <button onClick={() => sendEmail(it.id, it.title, tab === "petitions" ? "petition" : "flash")} data-testid={`admin-email-${it.id}`} title="Enviar por correo a los usuarios registrados" className="p-2 text-slate-500 hover:text-indigo-600"><Mail className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => del(it.id)} data-testid={`admin-delete-${it.id}`} className="p-2 text-slate-500 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {view === "analytics" && <AdminAnalytics />}

      {view === "reports" && (
        reports === null ? <Loading /> : (
          <div className="space-y-3">
            {reports.length === 0 ? <p className="text-slate-400 py-8 text-center">Bandeja vacía.</p> : reports.map((r) => (
              <div key={r.id} className="bg-white rounded-lg border border-slate-200 p-5" data-testid={`report-row-${r.id}`}>
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">{r.title}</p>
                    <p className="text-sm text-slate-600 mt-1">{r.description}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {r.category && `${r.category} · `}{r.agency && `${r.agency} · `}{r.anonymous ? "Anónimo" : "Identificado"}
                      {r.attachment_name && ` · Adjunto: ${r.attachment_name}`}
                    </p>
                  </div>
                  <select value={r.internal_status} onChange={(e) => updateReportStatus(r.id, e.target.value)}
                    data-testid={`report-status-${r.id}`} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                    {["recibido", "en_revision", "asignado", "cerrado"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {view === "proposals" && (
        proposals === null ? <Loading /> : (
          <div className="space-y-3">
            {proposals.length === 0 ? <p className="text-slate-400 py-8 text-center">No hay preguntas propuestas.</p> : proposals.map((p) => (
              <div key={p.id} className="bg-white rounded-lg border border-slate-200 p-5" data-testid={`proposal-row-${p.id}`}>
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">{p.question}</p>
                    {p.context && <p className="text-sm text-slate-600 mt-1">{p.context}</p>}
                    {p.source && <p className="text-xs text-slate-400 mt-1">Fuente: {p.source}</p>}
                    <p className="text-xs text-slate-400 mt-1">Proponente confidencial · {new Date(p.created_at).toLocaleDateString("es-PR")}</p>
                  </div>
                  <select value={p.review_status} onChange={(e) => updateProposalStatus(p.id, e.target.value)}
                    data-testid={`proposal-status-${p.id}`} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                    {["pendiente", "en_revision", "aprobada", "rechazada", "publicada"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {view === "users" && isSuper && (
        <div data-testid="admin-users">
          <form onSubmit={(e) => { e.preventDefault(); loadUsers(); }} className="flex gap-2 mb-4 max-w-md">
            <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} data-testid="user-search"
              placeholder="Buscar por nombre o email…" className="flex-1 border border-slate-300 rounded-lg px-4 py-2 text-sm" />
            <button type="submit" className="bg-slate-900 text-white text-sm font-semibold px-4 rounded-lg">Buscar</button>
          </form>
          {users === null ? <Loading /> : (
            <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr><th className="p-3">Usuario</th><th className="p-3">Rol</th><th className="p-3">Actividad</th><th className="p-3">Registro</th><th className="p-3 text-right">Acciones</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-slate-100" data-testid={`user-row-${u.user_id}`}>
                      <td className="p-3">
                        <p className="font-medium text-slate-800">{u.name}{u.user_id === user.user_id && <span className="text-xs text-cyan-600"> (tú)</span>}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </td>
                      <td className="p-3">
                        <select value={u.role} disabled={u.user_id === user.user_id} onChange={(e) => changeRole(u.user_id, e.target.value)}
                          data-testid={`user-role-${u.user_id}`} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm disabled:opacity-50">
                          {VALID_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td className="p-3 text-slate-500 text-xs">{u.votes} votos · {u.signatures} firmas</td>
                      <td className="p-3 text-slate-500 text-xs">{new Date(u.created_at).toLocaleDateString("es-PR")}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => deleteUser(u.user_id, u.email)} disabled={u.user_id === user.user_id}
                          data-testid={`user-delete-${u.user_id}`} className="p-2 text-slate-400 hover:text-rose-500 disabled:opacity-30">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <p className="p-8 text-center text-slate-400">Sin usuarios.</p>}
            </div>
          )}
          <p className="flex items-center gap-2 text-xs text-slate-400 mt-3"><ShieldAlert className="w-4 h-4" /> Eliminar un usuario borra sus votos y firmas de forma permanente. Los conteos se ajustan automáticamente.</p>
        </div>
      )}

      {view === "audit" && (
        audit === null ? <Loading /> : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto" data-testid="admin-audit">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr><th className="p-3">Fecha</th><th className="p-3">Actor</th><th className="p-3">Acción</th><th className="p-3">Colección</th><th className="p-3">Detalle</th></tr>
              </thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={a.audit_id || a.log_id || i} className="border-t border-slate-100">
                    <td className="p-3 text-slate-500 text-xs whitespace-nowrap">{new Date(a.at).toLocaleString("es-PR")}</td>
                    <td className="p-3 text-slate-700">{a.actor}</td>
                    <td className="p-3"><span className="text-xs bg-slate-100 px-2 py-1 rounded-full">{a.action}</span></td>
                    <td className="p-3 text-slate-500">{a.collection}</td>
                    <td className="p-3 text-slate-500 text-xs">{a.detail || a.target_id || a.model || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {audit.length === 0 && <p className="p-8 text-center text-slate-400">Sin registros de auditoría.</p>}
          </div>
        )
      )}

      {view === "whatsapp" && (
        waLogs === null ? <Loading /> : (
          <div data-testid="admin-whatsapp-logs">
            <p className="text-slate-500 mb-4 text-sm">Historial de envíos por WhatsApp. El estado de entrega se actualiza automáticamente vía Twilio (enviado → entregado → leído).</p>
            <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Encuesta</th><th className="p-3 text-center">Enviados</th>
                    <th className="p-3 text-center">Entregados</th><th className="p-3 text-center">Fallidos</th>
                    <th className="p-3 text-center">Pendientes</th><th className="p-3">Último envío</th>
                  </tr>
                </thead>
                <tbody>
                  {waLogs.map((w) => (
                    <tr key={w.flash_id} className="border-t border-slate-100" data-testid={`wa-log-${w.flash_id}`}>
                      <td className="p-3 font-medium text-slate-800">{w.title}</td>
                      <td className="p-3 text-center font-semibold text-slate-900">{w.total}</td>
                      <td className="p-3 text-center"><span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{w.delivered}</span></td>
                      <td className="p-3 text-center"><span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">{w.failed}</span></td>
                      <td className="p-3 text-center"><span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">{w.pending}</span></td>
                      <td className="p-3 text-slate-500 text-xs whitespace-nowrap">{new Date(w.last_at).toLocaleString("es-PR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {waLogs.length === 0 && <p className="p-8 text-center text-slate-400">Aún no se han enviado encuestas por WhatsApp.</p>}
            </div>
          </div>
        )
      )}

      {editing && (
        <EditorModal tab={tab} item={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadItems(); loadOverview(); }} />
      )}
    </div>
  );
}

function EditorModal({ tab, item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOut, setAiOut] = useState("");
  const schema = SCHEMAS[tab];
  const isNew = !item.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      // validate JSON fields
      const payload = { ...form };
      for (const [key, , type] of schema.fields) {
        if (type === "json" && typeof payload[key] === "string") {
          payload[key] = JSON.parse(payload[key] || "[]");
        }
        if (type === "number" && payload[key] === "") payload[key] = null;
      }
      if (isNew) await api.post(`/admin/${tab}`, payload);
      else await api.put(`/admin/${tab}/${item.id}`, payload);
      toast.success(isNew ? "Creado." : "Actualizado.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revisa los campos JSON. " + (e.message || ""));
    } finally { setBusy(false); }
  };

  const runAi = async (mode) => {
    if (aiText.trim().length < 20) { toast.error("Pega más texto para procesar."); return; }
    setAiBusy(true); setAiOut("");
    try {
      const r = await api.post("/admin/ai/summarize", { text: aiText, mode });
      setAiOut(r.data.result);
      toast.info(r.data.notice);
    } catch (e) { toast.error("IA no disponible: " + (e?.response?.data?.detail || "")); } finally { setAiBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 flex items-start justify-center overflow-y-auto p-4" data-testid="editor-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white rounded-t-xl">
          <h3 className="font-heading font-bold text-lg text-slate-900">{isNew ? "Crear" : "Editar"} · {schema.label}</h3>
          <button onClick={onClose} data-testid="editor-close" className="p-2 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {schema.fields.map(([key, label, type]) => (
            <div key={key}>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{label}</label>
              {type === "textarea" ? (
                <textarea value={form[key] || ""} onChange={(e) => set(key, e.target.value)} rows={3}
                  data-testid={`field-${key}`} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-500" />
              ) : type === "json" ? (
                <textarea value={typeof form[key] === "string" ? form[key] : JSON.stringify(form[key] || [], null, 2)}
                  onChange={(e) => set(key, e.target.value)} rows={4} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono outline-none focus:ring-2 focus:ring-cyan-500" />
              ) : type === "bool" ? (
                <input type="checkbox" checked={!!form[key]} onChange={(e) => set(key, e.target.checked)} data-testid={`field-${key}`} className="w-4 h-4 accent-cyan-500" />
              ) : type === "status" ? (
                <select value={form[key] || "borrador"} onChange={(e) => set(key, e.target.value)} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {EDITORIAL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : type === "promise_status" ? (
                <select value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {PROMISE_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : type === "petition_status" ? (
                <select value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {PETITION_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : type === "entity_type" ? (
                <select value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {["funcionario", "agencia", "contratista", "partido", "candidato"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : type === "content_type" ? (
                <select value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {["investigacion", "analisis", "opinion"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : type === "poll_type" ? (
                <select value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {["actualidad", "confianza", "prioridad", "reaccion", "evaluacion", "decision", "verificacion"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : type === "results_mode" ? (
                <select value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`field-${key}`}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {["auto", "hidden_until_close", "partial", "realtime"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input type={type === "number" ? "number" : "text"} value={form[key] ?? ""} onChange={(e) => set(key, type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                  data-testid={`field-${key}`} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-500" />
              )}
            </div>
          ))}

          {/* AI assistant */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Sparkles className="w-4 h-4 text-cyan-500" /> Asistente IA (borrador interno)</p>
            <textarea value={aiText} onChange={(e) => setAiText(e.target.value)} rows={3} data-testid="ai-input"
              placeholder="Pega texto de un documento para resumir o extraer datos…"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2" />
            <div className="flex gap-2">
              <button onClick={() => runAi("summary")} disabled={aiBusy} data-testid="ai-summarize" className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-full disabled:opacity-60">Resumir</button>
              <button onClick={() => runAi("extract")} disabled={aiBusy} data-testid="ai-extract" className="text-xs bg-slate-200 text-slate-700 px-3 py-1.5 rounded-full disabled:opacity-60">Extraer datos</button>
            </div>
            {aiBusy && <p className="text-xs text-slate-400 mt-2">Procesando…</p>}
            {aiOut && <div className="mt-3 bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap" data-testid="ai-output">{aiOut}<p className="text-[11px] text-amber-600 mt-2">Requiere revisión y aprobación humana antes de publicar.</p></div>}
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-xl">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-slate-600 font-medium hover:bg-slate-100">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="editor-save"
            className="bg-cyan-500 text-white font-semibold px-6 py-2.5 rounded-full hover:bg-cyan-600 transition-colors disabled:opacity-60">
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
